import * as React from 'react';
import Papa from 'papaparse';
import { Button, Modal } from '../..';
import api from '../../../utils/apigateway';
import chunkArray from '../../../utils/chunkArray';

type ModalProps = {
  onCloseModal: () => void;
  onImportCount: (count: number) => void;
  storeId: number;
  path: string;
};

type ImportState = {
  status: 'idle' | 'parsing' | 'uploading' | 'success' | 'error';
  error: string | null;
  progress: { current: number; total: number };
};

const CHUNK_SIZE = 500;
const MAX_RETRIES = 3;

async function uploadChunkWithRetry(
  chunkPath: string,
  chunk: Record<string, unknown>[],
  storeId: number,
  retries = MAX_RETRIES,
): Promise<number> {
  const payload = { bulk: chunk, storeId };

  for (let attempt = 1; attempt <= retries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await api.post(chunkPath, payload);
      if (data) {
        return chunk.length;
      }
      return 0;
    } catch (error) {
      if (attempt === retries) {
        throw error;
      }
      // Wait before retrying (exponential backoff)
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, 1000 * attempt);
      });
    }
  }
  return 0;
}

function ImportPrompt({
  onCloseModal,
  onImportCount,
  storeId,
  path,
}: ModalProps) {
  const [uploadedFile, setUploadedFile] = React.useState<File>();
  const [importState, setImportState] = React.useState<ImportState>({
    status: 'idle',
    error: null,
    progress: { current: 0, total: 0 },
  });

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { files } = event.target;

    if (files && files.length > 0) {
      setUploadedFile(files[0]);
      setImportState({
        status: 'idle',
        error: null,
        progress: { current: 0, total: 0 },
      });
    }
  };

  const handleImport = async () => {
    if (!uploadedFile) {
      setImportState(prev => ({
        ...prev,
        error: 'Please select a file to upload',
      }));
      return;
    }

    setImportState({
      status: 'parsing',
      error: null,
      progress: { current: 0, total: 0 },
    });

    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: async results => {
        const { data, errors } = results;

        if (errors.length > 0) {
          setImportState({
            status: 'error',
            error: `CSV parsing error: ${errors[0].message}`,
            progress: { current: 0, total: 0 },
          });
          return;
        }

        if (!data || data.length === 0) {
          setImportState({
            status: 'error',
            error: 'The CSV file is empty or has no valid data',
            progress: { current: 0, total: 0 },
          });
          return;
        }

        if (data.length > 2000) {
          setImportState({
            status: 'error',
            error: 'Maximum 2000 products can be uploaded at once',
            progress: { current: 0, total: 0 },
          });
          return;
        }

        const chunks = chunkArray(
          data as Record<string, unknown>[],
          CHUNK_SIZE,
        );
        const totalChunks = chunks.length;
        let totalUploaded = 0;

        setImportState({
          status: 'uploading',
          error: null,
          progress: { current: 0, total: totalChunks },
        });

        // Upload chunks sequentially to avoid overwhelming the server
        for (let i = 0; i < chunks.length; i += 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const uploaded = await uploadChunkWithRetry(
              path,
              chunks[i],
              storeId,
            );
            totalUploaded += uploaded;

            setImportState(prev => ({
              ...prev,
              progress: { current: i + 1, total: totalChunks },
            }));
          } catch (error) {
            setImportState({
              status: 'error',
              error: `Failed to upload batch ${
                i + 1
              }. ${totalUploaded} products were imported before the error.`,
              progress: { current: i, total: totalChunks },
            });
            return;
          }
        }

        setImportState({
          status: 'success',
          error: null,
          progress: { current: totalChunks, total: totalChunks },
        });

        onImportCount(totalUploaded);
        onCloseModal();
      },
      error: error => {
        setImportState({
          status: 'error',
          error: `Failed to read file: ${error.message}`,
          progress: { current: 0, total: 0 },
        });
      },
    });
  };

  const isProcessing =
    importState.status === 'parsing' || importState.status === 'uploading';

  const getButtonLabel = () => {
    if (importState.status === 'parsing') return 'Parsing CSV...';
    if (importState.status === 'uploading') {
      const { current, total } = importState.progress;
      return `Uploading... (${current}/${total})`;
    }
    return 'Upload & Continue';
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col px-5 pt-5 pb-7">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              Import Products
            </h3>

            <p className="mb-2 text-pos-gray text-xbase">
              Kindly follow the instructions below to successfully import your
              products:
            </p>

            <div className="text-pos-black text-sm mb-2 font-normal space-y-4">
              <div className="space-y-3">
                <p>
                  <strong>1.</strong> Click on the{' '}
                  <a
                    href="/assets/docx/inventory_import.csv"
                    className="text-pos-blue"
                  >
                    inventory_import.csv
                  </a>{' '}
                  below to download a sample template file.
                </p>

                <p>
                  <strong>2.</strong> Please fill your products information into
                  the CSV file. A maximum of 2000 products can be uploaded at
                  once.
                </p>

                <p>
                  <strong>3.</strong> Upload the completed file.
                </p>

                <p>
                  <strong>4.</strong> You will receive an alert after upload is
                  done. This will take some minutes depending on your products.
                </p>

                <p className="text-pos-black text-xbase font-semibold">
                  Download:{' '}
                  <a
                    href="/assets/docx/inventory_import.csv"
                    className="text-pos-blue font-normal"
                  >
                    inventory_import.csv
                  </a>
                </p>
              </div>

              <hr className="border-b-[1px] border-t-[0] border-[#d0d7de]" />

              <div className="!mt-3">
                <label
                  htmlFor="file"
                  className="text-pos-black font-semibold text-xbase block mb-1"
                >
                  Upload File
                </label>
                <input
                  type="file"
                  name="file"
                  id="file"
                  accept=".csv,text/csv"
                  onChange={handleFileInputChange}
                  disabled={isProcessing}
                />
              </div>

              {importState.error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {importState.error}
                </div>
              )}
            </div>

            <div className="mt-8 text-center flex flex-row ml-auto space-x-4">
              <Button
                label={getButtonLabel()}
                onClick={handleImport}
                loading={isProcessing}
                type="button"
                disabled={isProcessing || !uploadedFile}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[180px] h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default ImportPrompt;
